import { Injectable, inject } from '@angular/core';
import { HttpEvent } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpRequestDecorator } from '../core/triggerbee/http.request.decorator';
import { TriggerbeeUrlBuilder, TriggerbeeApi } from '../core/triggerbee/triggerbee-url-builder.service';
import { RequestResult } from '../core/triggerbee/request-result';
import {
  DataPlatformEventFilter,
  DataPlatformGraphRequest,
  DataPlatformGraphResponse,
  DataPlatformBreakdownRequest,
  DataPlatformBreakdownResponse,
  DataPlatformFunnelRequest,
  DataPlatformFunnelResponse,
  DataPlatformScalarRequest,
  DataPlatformScalarResponse,
  DataPlatformQueryRequest,
  withEventsWindow,
  DataPlatformQueryResponse,
  DataPlatformQueryOptions,
  DataPlatformLabelledQueryResponse,
  DataPlatformVisitsRequest,
  DataPlatformVisitsResponse,
  DataPlatformVisitStatusRequest,
  DataPlatformVisitStatusResponse,
  DataPlatformProfilesRequest,
  DataPlatformProfilesResponse,
  DataPlatformProfileDetailsRequest,
  DataPlatformProfileDetailsResponse,
  DataPlatformProfileSplitOptions,
  DataPlatformProfileSplitRequest,
  DataPlatformProfileSplitResponse,
  DataPlatformPropertiesRequest,
  DataPlatformPropertiesResponse,
  DataPlatformConsentEventsRequest,
  DataPlatformConsentEventsResponse,
  DataPlatformAudienceEvaluateRequest,
  DataPlatformAudienceEvaluateResponse,
  DataPlatformAudienceCountRequest,
  DataPlatformAudienceCountResponse,
  DataPlatformAudienceCountManyRequest,
  DataPlatformAudienceCountManyResponse,
  DataPlatformAudienceCountDailyRequest,
  DataPlatformAudienceCountDailyResponse,
  DataPlatformWidgetCampaignsRequest,
  DataPlatformWidgetCampaignsResponse,
  DataPlatformFilterOptions,
  DataPlatformInsightsSummaryRequest,
  DataPlatformInsightsSummaryResponse,
  DataPlatformInsightsChatRequest,
  DataPlatformInsightsChatResponse,
  DataPlatformAudienceSuggestionsRequest,
  DataPlatformAudienceSuggestionsResponse,
  DataPlatformCampaignSuggestionFeedbackRequest,
  DataPlatformCampaignSuggestionsRequest,
  DataPlatformCampaignSuggestionsResponse,
  DataPlatformBounceGraphRequest,
  DataPlatformBounceGraphResponse,
  DataPlatformHoldoutImpactGraphRequest,
  DataPlatformHoldoutImpactGraphResponse,
  DataPlatformConsentVersionDatesRequest,
  DataPlatformConsentVersionDatesResponse,
  DataPlatformContactDetailRequest,
  DataPlatformContactDetail,
  ContactImportUploadResponse,
  ContactImportValidateResponse,
  ContactImportStatus,
  ValidateDeleteProfilesResponse,
  DeleteProfilesResponse,
  DataPlatformSalesDriversSiteSummaryRequest,
  DataPlatformSalesDriversSiteResponse,
  DataPlatformSalesDriversSiteSummaryDailyRequest,
  DataPlatformSalesDriversSiteDailyResponse,
  DataPlatformSalesDriversWidgetRequest,
  DataPlatformSalesDriversWidgetResponse,
  DataPlatformSalesDriversWidgetDailyRequest,
  DataPlatformSalesDriversWidgetDailyResponse,
  DataPlatformCustomerFeedbackScoresRequest,
  DataPlatformCustomerFeedbackScoresResponse,
  DataPlatformCustomerFeedbackScoresDailyRequest,
  DataPlatformCustomerFeedbackScoresDailyResponse,
  DataPlatformCustomerFeedbackByCustomerRequest,
  DataPlatformCustomerFeedbackByCustomerResponse,
  DataPlatformBenchmarkTimeframe,
  DataPlatformBenchmarksResponse,
  DataPlatformFormResponseDatapointsRequest,
  DataPlatformFormResponseDatapointsResponse,
  DP_METRICS,
} from './data-platform.model';

@Injectable()
export class DataPlatformService {
  private readonly http = inject(HttpRequestDecorator);
  private readonly urlBuilder = inject(TriggerbeeUrlBuilder);
  public filterBS = new BehaviorSubject<DataPlatformEventFilter | undefined>(undefined);
  public granularityBS = new BehaviorSubject<string>('day');
  public readonly refreshBS = new Subject<void>();
  /** A range picked on a page that renders its own date pill; the shell applies it. */
  public readonly rangeChangeBS = new Subject<{ start: Date; end: Date }>();

  private filterOptionsCache: { key: string; options: DataPlatformFilterOptions; expiresAt: number } | null = null;
  private static readonly FILTER_OPTIONS_TTL_MS = 5 * 60 * 1000;
  // Profile exports stream up to 1M rows, so they need a far longer ceiling than a normal request.
  private static readonly EXPORT_PROFILES_TIMEOUT_MS = 15 * 60 * 1000;
  // Visits run several times larger than profiles, so they get longer again.
  private static readonly EXPORT_VISITS_TIMEOUT_MS = 30 * 60 * 1000;

  /** `feature` names the requesting UI surface as `{page}-{section}` (e.g. `visits-list`) — without the `client-` prefix, which is added here. */
  private post<T>(endpoint: string, body: unknown, feature: string): Observable<RequestResult<T>> {
    const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, endpoint, { feature: `client-${feature}` });
    return this.http.post(url, body).pipe(
      map((data) => new RequestResult<T>(null, data, 200)),
      catchError((e) => of(new RequestResult<T>(e.error, null, e.status ?? 0))),
    );
  }

  getGraph(request: DataPlatformGraphRequest, feature: string): Observable<RequestResult<DataPlatformGraphResponse>> {
    return this.post('data-platform/events/graph', request, feature);
  }

  getBounceGraph(
    request: DataPlatformBounceGraphRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformBounceGraphResponse>> {
    return this.post('data-platform/events/bounce-graph', request, feature);
  }

  getHoldoutImpactGraph(
    request: DataPlatformHoldoutImpactGraphRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformHoldoutImpactGraphResponse>> {
    return this.post('data-platform/events/holdout-impact-graph', request, feature);
  }

  getBreakdown(
    request: DataPlatformBreakdownRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformBreakdownResponse>> {
    return this.post('data-platform/events/breakdown', request, feature);
  }

  getFunnel(
    request: DataPlatformFunnelRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformFunnelResponse>> {
    return this.post('data-platform/events/funnel', request, feature);
  }

  getScalar(
    request: DataPlatformScalarRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformScalarResponse>> {
    return this.post('data-platform/events/scalar', request, feature);
  }

  /** Generic analytics query engine — one call composes scalar / time-series / breakdown / grouped-series shapes. */
  query(
    request: DataPlatformQueryRequest,
    feature: string,
    options: DataPlatformQueryOptions = {},
  ): Observable<RequestResult<DataPlatformQueryResponse>> {
    return this.post('data-platform/query', options.wholeWindow ? request : withEventsWindow(request), feature);
  }

  /** {@link query}, with a widget_id breakdown's widgets named across every account in the filter. */
  queryLabelled(
    request: DataPlatformQueryRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformLabelledQueryResponse>> {
    return this.post('data-platform/query/labelled', withEventsWindow(request), feature);
  }

  getVisits(
    request: DataPlatformVisitsRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformVisitsResponse>> {
    return this.post('data-platform/events/visits', request, feature);
  }

  /** The total alone, as its own request so the list never waits for it. */
  getVisitsCount(
    filter: DataPlatformEventFilter,
    feature: string,
  ): Observable<RequestResult<DataPlatformVisitsResponse>> {
    const request: DataPlatformVisitsRequest = {
      filter,
      page: 1,
      pageSize: 1,
      includeTotalCount: true,
      countOnly: true,
      includeEvents: false,
    };
    return this.post('data-platform/events/visits', request, feature);
  }

  getProfiles(
    request: DataPlatformProfilesRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformProfilesResponse>> {
    return this.post('data-platform/events/profiles', request, feature);
  }

  /** Fetches only the total count for a filter, as its own request so it never blocks page rendering. */
  getProfilesCount(
    filter: DataPlatformEventFilter,
    includeInactive: boolean | undefined,
    notYetSeen: boolean | undefined,
    feature: string,
  ): Observable<RequestResult<DataPlatformProfilesResponse>> {
    return this.post(
      'data-platform/events/profiles',
      { filter, page: 1, pageSize: 1, includeTotalCount: true, countOnly: true, includeInactive, notYetSeen },
      feature,
    );
  }

  /** Streamed CSV export (progress events + final Blob); sends the current filter so it matches the list. */
  exportProfiles(
    filter: DataPlatformEventFilter,
    sortDirection: 'asc' | 'desc',
    includeInactive: boolean | undefined,
    notYetSeen: boolean | undefined,
    feature: string,
  ): Observable<HttpEvent<Blob>> {
    const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, 'data-platform/events/profiles/export', {
      feature: `client-${feature}`,
    });
    return this.http.postBlobDownloadEvents(
      url,
      { filter, sortDirection, includeInactive, notYetSeen },
      DataPlatformService.EXPORT_PROFILES_TIMEOUT_MS,
    );
  }

  /** The account's export row cap (its monthly import/export limit, or a fallback ceiling). */
  getProfilesExportRowLimit(feature: string): Observable<RequestResult<{ rowLimit: number }>> {
    return this.get('data-platform/events/profiles/export/row-limit', feature);
  }

  /** Streamed CSV export of the visits list; sends the current filter so the file matches the screen. */
  exportVisits(
    filter: DataPlatformEventFilter,
    sortDirection: 'asc' | 'desc',
    feature: string,
  ): Observable<HttpEvent<Blob>> {
    const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, 'data-platform/events/visits/export', {
      feature: `client-${feature}`,
    });
    return this.http.postBlobDownloadEvents(
      url,
      { filter, sortDirection, includeEvents: false },
      DataPlatformService.EXPORT_VISITS_TIMEOUT_MS,
    );
  }

  getProfileDetails(
    request: DataPlatformProfileDetailsRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformProfileDetailsResponse>> {
    return this.post('data-platform/events/profiles/details', request, feature);
  }

  getProperties(
    request: DataPlatformPropertiesRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformPropertiesResponse>> {
    return this.post('data-platform/events/properties', request, feature);
  }
  getVisitStatus(
    request: DataPlatformVisitStatusRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformVisitStatusResponse>> {
    return this.post('data-platform/events/visits/status', request, feature);
  }

  getConsentEvents(
    request: DataPlatformConsentEventsRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformConsentEventsResponse>> {
    return this.post('data-platform/events/consent-events', request, feature);
  }

  getConsentVersionDates(
    request: DataPlatformConsentVersionDatesRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformConsentVersionDatesResponse>> {
    return this.post('data-platform/events/consent-version-dates', request, feature);
  }

  getWidgetCampaigns(
    request: DataPlatformWidgetCampaignsRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformWidgetCampaignsResponse>> {
    return this.post('data-platform/events/widget-campaigns', request, feature);
  }

  evaluateAudiences(
    request: DataPlatformAudienceEvaluateRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformAudienceEvaluateResponse>> {
    return this.post('data-platform/audiences/evaluate', request, feature);
  }

  countAudience(
    request: DataPlatformAudienceCountRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformAudienceCountResponse>> {
    return this.post('data-platform/audiences/count', request, feature);
  }

  countAudiences(
    request: DataPlatformAudienceCountManyRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformAudienceCountManyResponse>> {
    return this.post('data-platform/audiences/count/many', request, feature);
  }

  countAudienceDaily(
    request: DataPlatformAudienceCountDailyRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformAudienceCountDailyResponse>> {
    return this.post('data-platform/audiences/count/daily', request, feature);
  }

  getFilterOptions(filter: DataPlatformEventFilter, feature: string): Observable<DataPlatformFilterOptions> {
    const key = JSON.stringify(filter);
    const now = Date.now();
    if (this.filterOptionsCache && this.filterOptionsCache.key === key && this.filterOptionsCache.expiresAt > now) {
      return of(this.filterOptionsCache.options);
    }

    return forkJoin({
      goals: this.getBreakdown({ filter, dimension: 'goal_name', topN: 100 }, feature),
      tags: this.getBreakdown({ filter, dimension: 'tags', topN: 100 }, feature),
      campaigns: this.getBreakdown({ filter, dimension: 'utm_campaign_name', topN: 100 }, feature),
      widgetCampaigns: this.getWidgetCampaigns({ filter }, feature),
      dimensions: this.getBreakdown({ filter, dimension: 'dimensions', topN: 100 }, feature),
      eventTypes: this.getBreakdown({ filter, dimension: 'event_type', topN: 100 }, feature),
      consentVersions: this.getConsentVersionDates({ siteIds: filter.siteIds }, feature).pipe(
        catchError(() => of(null)),
      ),
      countries: this.getBreakdown({ filter, dimension: 'geo_country', topN: 100 }, feature),
      sessionSources: this.getBreakdown({ filter, dimension: 'session_source', topN: 100 }, feature),
      referrerApps: this.getBreakdown({ filter, dimension: 'referrer_app', topN: 100 }, feature),
      // Session rows only, so sites rank by visits rather than events.
      referrerDomains: this.getBreakdown(
        { filter, dimension: 'referrer_domain', eventType: 'session', topN: 100 },
        feature,
      ),
      utmSources: this.getBreakdown({ filter, dimension: 'utm_campaign_source', topN: 100 }, feature),
      utmMediums: this.getBreakdown({ filter, dimension: 'utm_campaign_medium', topN: 100 }, feature),
      identitySources: this.getBreakdown({ filter, dimension: 'identity_source', topN: 100 }, feature),
      deviceTypes: this.getBreakdown({ filter, dimension: 'device_type', topN: 100 }, feature),
      loginStates: this.query({ filter, metrics: [DP_METRICS.determinedSessions] }, feature),
    }).pipe(
      map((results) => {
        const labels = (result: RequestResult<DataPlatformBreakdownResponse> | null | undefined) =>
          (result?.data?.rows ?? []).map((r) => r.label).filter((l) => !!l);
        const eventTypeLabels = labels(results.eventTypes);
        // An API without the dimension falls back to event_type; that list is not data.
        const unlessEventTypes = (list: string[]) =>
          list.length > 0 && list.every((l) => eventTypeLabels.includes(l)) ? [] : list;
        const determinedSessions = results.loginStates?.data?.rows?.[0]?.[DP_METRICS.determinedSessions];
        const options: DataPlatformFilterOptions = {
          goalOptions: labels(results.goals).filter((l) => !l.startsWith('oc_')),
          tagOptions: labels(results.tags),
          utmCampaignOptions: labels(results.campaigns),
          dimensionOptions: labels(results.dimensions),
          widgetEventTypeOptions: eventTypeLabels.filter((l) => l.startsWith('widget-')),
          consentVersionOptions: results.consentVersions?.data?.versions ?? [],
          countryOptions: labels(results.countries),
          sessionSourceOptions: labels(results.sessionSources),
          referrerAppOptions: labels(results.referrerApps),
          referrerDomainOptions: labels(results.referrerDomains),
          deviceTypeOptions: labels(results.deviceTypes),
          hasLoginData: typeof determinedSessions === 'number' ? determinedSessions > 0 : null,
          utmSourceOptions: unlessEventTypes(labels(results.utmSources)),
          utmMediumOptions: unlessEventTypes(labels(results.utmMediums)),
          identitySourceOptions: unlessEventTypes(labels(results.identitySources)),
          hasNpsScores: eventTypeLabels.includes('nps-score'),
          hasCsatScores: eventTypeLabels.includes('csat-score'),
        };
        this.filterOptionsCache = { key, options, expiresAt: now + DataPlatformService.FILTER_OPTIONS_TTL_MS };
        return options;
      }),
    );
  }

  getSummary(
    request: DataPlatformInsightsSummaryRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformInsightsSummaryResponse>> {
    return this.post('data-platform/insights/summary', request, feature);
  }

  chat(
    request: DataPlatformInsightsChatRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformInsightsChatResponse>> {
    return this.post('data-platform/insights/chat', request, feature);
  }

  suggestAudiences(
    request: DataPlatformAudienceSuggestionsRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformAudienceSuggestionsResponse>> {
    return this.post('data-platform/insights/audience-suggestions', request, feature);
  }

  suggestCampaignChanges(
    campaignId: number,
    request: DataPlatformCampaignSuggestionsRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformCampaignSuggestionsResponse>> {
    return this.post(`data-platform/insights/campaign-suggestions/${campaignId}`, request, feature);
  }

  rateCampaignSuggestion(
    campaignId: number,
    request: DataPlatformCampaignSuggestionFeedbackRequest,
    feature: string,
  ): Observable<RequestResult<void>> {
    return this.post(`data-platform/insights/campaign-suggestions/${campaignId}/feedback`, request, feature);
  }

  private get<T>(
    endpoint: string,
    feature: string,
    params: Record<string, string | number> = {},
  ): Observable<RequestResult<T>> {
    const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, endpoint, { ...params, feature: `client-${feature}` });
    return this.http.get(url).pipe(
      map((data) => new RequestResult<T>(null, data, 200)),
      catchError((e) => of(new RequestResult<T>(e.error, null, e.status ?? 0))),
    );
  }

  getContactDetail(
    request: DataPlatformContactDetailRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformContactDetail>> {
    return this.post('data-platform/contacts/detail', request, feature);
  }

  /** The gateway fans this out to both stores, so this also lands in CustomerProfiles. */
  importContacts(
    formData: FormData,
    clearDataWhenEmptyColumns: boolean,
    feature: string,
  ): Observable<RequestResult<ContactImportUploadResponse>> {
    const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, 'data-platform/contacts/import', {
      feature: `client-${feature}`,
    });
    return this.http.upload(url, formData, { clearDataWhenEmptyColumns }).pipe(
      map((data) => new RequestResult<ContactImportUploadResponse>(null, data, 200)),
      catchError((e) => of(new RequestResult<ContactImportUploadResponse>(e.error, null, e.status ?? 0))),
    );
  }

  /** Parsed by DataPlatform, which is where this view's import runs. */
  validateImport(formData: FormData, feature: string): Observable<RequestResult<ContactImportValidateResponse>> {
    const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, 'data-platform/contacts/import/validate', {
      feature: `client-${feature}`,
    });
    return this.http.upload(url, formData).pipe(
      map((data) => new RequestResult<ContactImportValidateResponse>(null, data, 200)),
      catchError((e) => of(new RequestResult<ContactImportValidateResponse>(e.error, null, e.status ?? 0))),
    );
  }

  getImportStatus(importId: number, feature: string): Observable<RequestResult<ContactImportStatus>> {
    return this.get(`data-platform/contacts/import/${importId}`, feature);
  }

  getSalesDriversSite(
    request: DataPlatformSalesDriversSiteSummaryRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformSalesDriversSiteResponse>> {
    return this.post('data-platform/sales-drivers/site', request, feature);
  }

  getSalesDriversSiteDaily(
    request: DataPlatformSalesDriversSiteSummaryDailyRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformSalesDriversSiteDailyResponse>> {
    return this.post('data-platform/sales-drivers/site/daily', request, feature);
  }

  getSalesDriversWidget(
    request: DataPlatformSalesDriversWidgetRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformSalesDriversWidgetResponse>> {
    return this.post('data-platform/sales-drivers/widget', request, feature);
  }

  getSalesDriversWidgetDaily(
    request: DataPlatformSalesDriversWidgetDailyRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformSalesDriversWidgetDailyResponse>> {
    return this.post('data-platform/sales-drivers/widget/daily', request, feature);
  }

  getCustomerFeedbackScores(
    request: DataPlatformCustomerFeedbackScoresRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformCustomerFeedbackScoresResponse>> {
    return this.post('data-platform/customer-feedback/scores', request, feature);
  }

  getCustomerFeedbackScoresDaily(
    request: DataPlatformCustomerFeedbackScoresDailyRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformCustomerFeedbackScoresDailyResponse>> {
    return this.post('data-platform/customer-feedback/scores/daily', request, feature);
  }

  getCustomerFeedbackByCustomer(
    request: DataPlatformCustomerFeedbackByCustomerRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformCustomerFeedbackByCustomerResponse>> {
    return this.post('data-platform/customer-feedback/by-customer', request, feature);
  }

  getBenchmarks(
    months: DataPlatformBenchmarkTimeframe,
    feature: string,
  ): Observable<RequestResult<DataPlatformBenchmarksResponse>> {
    return this.get('data-platform/benchmarks', feature, { months });
  }

  getFormResponseDatapoints(
    request: DataPlatformFormResponseDatapointsRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformFormResponseDatapointsResponse>> {
    return this.post('data-platform/form-response-analytics/datapoints', request, feature);
  }

  /** Deletes from both stores: the gateway fans out, as the legacy Delete profiles page does. */
  deleteProfilesByIdentifiers(
    identifiers: string,
    deleteInGroupedAccounts: boolean,
    feature: string,
  ): Observable<RequestResult<void>> {
    return this.post('data-platform/contacts/delete', { identifiers, deleteInGroupedAccounts }, feature);
  }

  validateDeleteProfilesByIdentifiers(
    identifiers: string,
    deleteInGroupedAccounts: boolean,
    feature: string,
  ): Observable<RequestResult<ValidateDeleteProfilesResponse>> {
    return this.post('data-platform/contacts/delete/validate', { identifiers, deleteInGroupedAccounts }, feature);
  }

  /** Cutoff is sent as yyyy-MM-dd; the server resolves who counts as inactive. */
  deleteProfilesByDate(deleteOlderThan: string, feature: string): Observable<RequestResult<DeleteProfilesResponse>> {
    return this.post('data-platform/contacts/delete/by-date', { deleteOlderThan }, feature);
  }

  validateDeleteProfilesByDate(
    deleteOlderThan: string,
    feature: string,
  ): Observable<RequestResult<ValidateDeleteProfilesResponse>> {
    return this.post('data-platform/contacts/delete/by-date/validate', { deleteOlderThan }, feature);
  }

  /** Single person, keyed by uid because that is what the Profiles list holds. */
  deleteProfile(uid: number, feature: string): Observable<RequestResult<void>> {
    const url = this.urlBuilder.buildUrl(TriggerbeeApi.Gateway, `data-platform/contacts/profiles/${uid}`, {
      feature: `client-${feature}`,
    });
    return this.http.delete(url).pipe(
      map((data) => new RequestResult<void>(null, data as void, 200)),
      catchError((e) => of(new RequestResult<void>(e.error, null, e.status ?? 0))),
    );
  }

  /** Triggerbee admins only. */
  getProfileSplitOptions(uid: number, feature: string): Observable<RequestResult<DataPlatformProfileSplitOptions>> {
    return this.get(`data-platform/contacts/profiles/${uid}/split`, feature);
  }

  /** Triggerbee admins only. */
  splitProfile(
    uid: number,
    request: DataPlatformProfileSplitRequest,
    feature: string,
  ): Observable<RequestResult<DataPlatformProfileSplitResponse>> {
    return this.post(`data-platform/contacts/profiles/${uid}/split`, request, feature);
  }
}
